import { Injectable } from '@angular/core';
import { FormBuilder, Validators } from '@angular/forms';
import { Note } from '@app/models/note.model';
import { Profile } from '@app/models/profile.model';
import { ScheduleField } from '@app/models/schedule.model';
import { Staff } from '@app/models/staff.model';
import { DateService } from './date.service';

const statusList = [
  { resultatr: 1, value: '計画' },
  { resultatr: 2, value: '実施' },
  { resultatr: 3, value: '中止' },
];

interface NoteDate {
  sdate: Date | string | null;
  edate: Date | string | null;
}

interface FormDate {
  startDate: { date: Date; time: string };
  endDate: { date: Date; time: string };
}

interface FormValue extends FormDate {
  summary: boolean;
  handover: boolean;
  status: string;
  odrstfcd: string | null;
  sndstfcd: string | null;
  subject: string;
}

interface DefaultStaff {
  odrstfcd?: string;
  odrstfnm?: string;
  sndstfcd?: string;
  sndstfnm?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ScheduleService {
  constructor(private fb: FormBuilder, private dateService: DateService) {}

  getStatus(resultatr: number): string {
    const status = statusList.find((item) => item.resultatr === resultatr);
    return status?.value ?? '';
  }

  getResultatr(value: string): number {
    const status = statusList.find((item) => item.value === value);
    return status?.resultatr ?? 0;
  }

  isImplementation(resultatr: number): boolean {
    return resultatr === 2;
  }

  isCancel(resultatr: number): boolean {
    return resultatr === 3;
  }

  getSchedule(noteList: Note[]): Note | undefined {
    return noteList.find((item) => item.notekbn === 4);
  }

  setScheduleForm(
    profile: Profile | null,
    {
      lifeflg,
      pickupflg,
      sdate,
      edate,
      resultatr,
      odrstfcd,
      sndstfcd,
    }: ScheduleField
  ) {
    return this.fb.group({
      summary: Boolean(lifeflg),
      handover: Boolean(pickupflg),
      startDate: this.fb.group({
        date: [sdate, Validators.required],
        time: [
          sdate ? this.dateService.format(sdate, 'HH:mm') : null,
          Validators.required,
        ],
      }),
      endDate: this.fb.group({
        date: [edate, Validators.required],
        time: [
          edate ? this.dateService.format(edate, 'HH:mm') : null,
          Validators.required,
        ],
      }),
      status: this.getStatus(resultatr || 2), // 2: 実施
      odrstfcd: odrstfcd ?? profile?.staffCode,
      sndstfcd,
    });
  }

  getFormValue(
    {
      summary,
      handover,
      startDate,
      endDate,
      status,
      odrstfcd,
      sndstfcd,
      subject
    }: FormValue,
    getStaffName: (stafcd: string) => string | null
  ) {
    return {
      lifeflg: Number(summary),
      pickupflg: Number(handover),
      sdate: this.dateService.parseDateForm(startDate),
      edate: this.dateService.parseDateForm(endDate),
      resultatr: this.getResultatr(status),
      odrstfcd,
      odrstfnm: getStaffName(odrstfcd || ''),
      sndstfcd,
      sndstfnm: getStaffName(sndstfcd || ''),
      subject
    };
  }

  getStaffName(staffList?: Staff[]) {
    return ({ odrstfcd, odrstfnm, sndstfcd, sndstfnm }: DefaultStaff) => {
      return (stfcd: string) => {
        const selected = staffList?.find((staff) => staff.stfcd === stfcd);
        if (selected) return selected.name;
        if (odrstfnm && stfcd === odrstfcd) return odrstfnm;
        if (sndstfnm && stfcd === sndstfcd) return sndstfnm;
        return null;
      };
    };
  }

  setDefaultStaff(
    { odrstfcd, odrstfnm, sndstfcd, sndstfnm }: DefaultStaff,
    staffList: Staff[]
  ) {
    if (odrstfcd && odrstfnm) {
      const hasStaff = staffList.some((item) => item.stfcd === odrstfcd);
      if (!hasStaff) {
        return [...staffList, { stfcd: odrstfcd, name: odrstfnm }];
      }
    }
    if (sndstfcd && sndstfnm) {
      const hasStaff = staffList.some((item) => item.stfcd === sndstfcd);
      if (!hasStaff) {
        return [...staffList, { stfcd: sndstfcd, name: sndstfnm }];
      }
    }
    return staffList;
  }

  parseDateForm({ startDate, endDate }: FormDate) {
    const sdate = this.dateService.parseDateForm(startDate);
    const edate = this.dateService.parseDateForm(endDate);
    return { sdate, edate };
  }

  outScheduleDate(schedule: NoteDate) {
    const getTime = (date: Date | string) => {
      const d = new Date(date);
      return d.getTime();
    };

    const scheduleStart = schedule.sdate ? getTime(schedule.sdate) : null;
    const scheduleEnd = schedule.edate ? getTime(schedule.edate) : null;

    return ({ sdate, edate }: NoteDate) => {
      if (!scheduleStart || !scheduleEnd || !sdate || !edate) return false;

      const recordStart = getTime(sdate);
      const recordEnd = getTime(edate);

      return (
        recordStart < scheduleStart ||
        recordStart >= scheduleEnd ||
        recordEnd > scheduleEnd ||
        recordEnd < scheduleStart
      );
    };
  }
}
